import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/core';
import crypto from 'crypto';
import { utils } from 'eciesjs';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import RevealSeed from '../../components/RevealSeed';
import { RootState } from '../../store/reducers';
import WalletPasswordSetup from '../Wallet/WalletPasswordSetup';

const checkPassword = (password) => {
  try {
    const passwordKey = crypto.createHash('sha256').update(password).digest();
    const encryptedMnemonic = window.localStorage.getItem('encryptedMnemonic');

    utils
      .aesDecrypt(passwordKey, Buffer.from(encryptedMnemonic!, 'base64'))
      .toString('utf8');
    return true;
  } catch (e) {
    return false;
  }
};

const ViewSeedPhraseSetting = () => {
  const mnemonic = useSelector((state: RootState) => state.wallet.mnemonic);

  const [forceRevealed, setForceRevealed] = useState(false);
  const [showSeedPasswordInput, setShowSeedPasswordInput] = useState(false);

  const [seedInputPassword, setSeedInputPassword] = useState('');
  const [seedInputError, setSeedInputError] = useState(false);

  const handleShowSeed = () => {
    if (checkPassword(seedInputPassword)) {
      setSeedInputError(false);
      setForceRevealed(true);
    } else {
      setSeedInputError(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleShowSeed();
    }
  };

  return (
    <>
      <Text fontSize="sm" mb={2}>
        If you ever change browsers or computers, you will need this seed phrase
        to access your bets. Save it somewhere safe and secure.
      </Text>
      <RevealSeed
        mnemonic={mnemonic}
        revealable={false}
        forceReveal={forceRevealed}
        onAttemptReveal={() => setShowSeedPasswordInput(true)}
        maxW="500px"
      />

      {showSeedPasswordInput && !forceRevealed && (
        <Flex mt={4}>
          <Box>
            <FormLabel fontSize="sm">
              Enter wallet password to continue
            </FormLabel>
            <InputGroup>
              <Input
                placeholder="Enter Password Here"
                type="password"
                value={seedInputPassword}
                isInvalid={seedInputError}
                onChange={(e) => setSeedInputPassword(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <InputRightAddon
                width="4.5rem"
                cursor="pointer"
                onClick={handleShowSeed}
                bg="blue.600"
                color="white"
                borderColor="black"
                transition="opacity 0.1s linear"
                _hover={{ opacity: 0.75 }}
              >
                Confirm
              </InputRightAddon>
            </InputGroup>
          </Box>
        </Flex>
      )}
    </>
  );
};

const ChangePassword = () => {
  const [oldPWConfirmed, setOldPWConfirmed] = useState(false);

  const [oldPWInput, setOldPWInput] = useState('');
  const [oldPWInputError, setOldPWInputError] = useState(false);

  const [passowrdChanged, setPasswordChanged] = useState(false);

  const handleCheckPassword = () => {
    if (checkPassword(oldPWInput)) {
      setOldPWInputError(false);
      setOldPWConfirmed(true);
    } else {
      setOldPWInputError(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleCheckPassword();
    }
  };

  if (passowrdChanged) {
    return <Text>Password successfully changed!</Text>;
  }

  if (!oldPWConfirmed)
    return (
      <Flex>
        <Box>
          <FormLabel fontSize="sm">Enter wallet password to continue</FormLabel>
          <InputGroup>
            <Input
              placeholder="Enter Password Here"
              type="password"
              value={oldPWInput}
              isInvalid={oldPWInputError}
              onChange={(e) => setOldPWInput(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <InputRightAddon
              width="4.5rem"
              cursor="pointer"
              onClick={handleCheckPassword}
              bg="blue.600"
              color="white"
              borderColor="black"
              transition="opacity 0.1s linear"
              _hover={{ opacity: 0.75 }}
            >
              Confirm
            </InputRightAddon>
          </InputGroup>
        </Box>
      </Flex>
    );

  return (
    <WalletPasswordSetup
      onNext={() => {
        setPasswordChanged(true);
      }}
    />
  );
};

const WalletSettingsModal = ({ isOpen, onClose }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay>
        <ModalContent minH="360px" maxW="640px">
          <ModalHeader>Wallet Settings</ModalHeader>
          <ModalCloseButton />

          <ModalBody p={4}>
            <Accordion defaultIndex={0} borderColor="#f5f6f9">
              <AccordionItem>
                <AccordionButton bg="#f5f6f9">
                  <Box flex="1" textAlign="left" fontWeight="500">
                    View Seed Phrase
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <ViewSeedPhraseSetting />
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem mt={4}>
                <AccordionButton bg="#f5f6f9">
                  <Box flex="1" textAlign="left" fontWeight="500">
                    Change Atomic Odds Wallet Password
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <ChangePassword />
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  );
};

export default WalletSettingsModal;
